import request from 'umi-request'
import Cookies from 'js-cookie'
import {
    addChoices,
    addLanguage,
    addUser,
    logoutUser,
    addUserPermissions,
    addAllowedModules,
    addCompany,
    addDateFormat,
    addTimezoneFormat,
    addPrefSwitchToCandidate,
    addAutoTimesheetApproval,
    addAllowJumpOverStages,
    addAllowEmailNotifications,
    addAllowQRScanner,
    addAllowedOrderTypes,
    addDefaultOrderTypes,
    addPreferedDashboard,
} from '../actions'
import { APIRequest, requests } from './helpers/requests'
import { normalizeUiLanguage } from './helpers/languages'
import { API } from '../config'
import { userService } from './ServiceUser'
import { commonService, getRequest } from './ServiceCommon'

/*
 * Service functions
 */
export const authService = {
    login,
    logout,
    refreshLogin,
    register,
    resendActivation,
    getAccessToken,
    validateToken,
    getUserPermissions,
    getAllowedModules,
    startSimulationSession,
    endSimulationSession,
}

async function syncSessionAfterTokenExchange(response, dispatch) {
    setAuthTokens(response)
    const userMe = await userService.getUserProfile()
    if (userMe.status === 200) {
        dispatch(addUser(userMe.response))
    }
    const permissions = await getUserPermissions()
    if (permissions.status === 200) {
        dispatch(addUserPermissions(permissions.response))
    }
}

async function startSimulationSession(role, permissionGroupIds, dispatch) {
    const result = await APIRequest({
        url: API.USERS_ME + 'simulation/',
        method: 'POST',
        data: { role, permission_group_ids: permissionGroupIds },
        private: true,
    })
    if (result.status === 200) {
        await syncSessionAfterTokenExchange(result.response, dispatch)
    }
    return result
}

async function endSimulationSession(dispatch) {
    const result = await APIRequest({
        url: API.USERS_ME + 'simulation/',
        method: 'DELETE',
        private: true,
    })
    if (result.status === 200) {
        await syncSessionAfterTokenExchange(result.response, dispatch)
    }
    return result
}

function setAuthTokens(response) {
    let tmpExpiration = response.expiresIn
    let expiresIn = new Date(new Date().getTime() + tmpExpiration * 1000)

    if (document.location.protocol === 'https:') {
        Cookies.set('accessToken', response.access, {
            expires: expiresIn,
            secure: true,
        })
        Cookies.set('refreshToken', response.refresh, { secure: true })
    } else {
        Cookies.set('accessToken', response.access, { expires: expiresIn })
        Cookies.set('refreshToken', response.refresh)
    }
}

/* Auth Services */
async function getUserPermissions() {
    let result = await APIRequest({
        url: API.USERS_ME + 'permissions/',
        method: 'GET',
        private: true,
    })

    return result
}

async function getAllowedModules() {
    let result = await APIRequest({
        url: API.CONFIG + 'allowed_modules/',
        method: 'GET',
        private: true,
    })

    return result
}

async function login(username, password, dispatch, i18n) {
    let result = await APIRequest({
        url: API.LOGIN,
        method: 'POST',
        data: {
            username: username,
            password: password,
        },
        private: true,
    })

    if (result.status === 200) {
        setAuthTokens(result.response)
        const userMe = await userService.getUserProfile()
        if (userMe.status === 200) {
            dispatch(addUser(userMe.response))
            result['role'] = userMe.response.role
        } else {
            dispatch(addUser(result.response.user))
        }

        const permissions = await getUserPermissions()
        if (permissions.status === 200) {
            dispatch(addUserPermissions(permissions.response))

            if (permissions.response.includes('users.c_terminal_warehouse')) {
                result['can_access_warehouse'] = true
            }
        }

        const allowedModules = await getAllowedModules()
        if (allowedModules.status === 200) {
            dispatch(addAllowedModules(allowedModules.response))
        }

        // set preferences
        const globalPreferences = await requests.get(API.PREFERENCES)
        const userPreferences = await requests.get(API.USER_PREFERENCES)

        if (globalPreferences.status === 200) {
            const globalPrefResponse = globalPreferences.response

            const date_format = globalPrefResponse.find(
                (item) => item.identifier === 'date_format__global_date_format_preferences'
            )
            if (date_format) {
                dispatch(addDateFormat(date_format.value))
            }

            const order_types = globalPrefResponse.find((item) => item.identifier === 'display_orders__display_orders')
            if (order_types) {
                dispatch(addAllowedOrderTypes(order_types.value))
            }

            const order_default_types = globalPrefResponse.find(
                (item) => item.identifier === 'display_orders__display_type_orders'
            )
            if (order_default_types) {
                dispatch(addDefaultOrderTypes(order_default_types.value))
            }

            const switch_to_employee = globalPrefResponse.find(
                (item) => item.identifier === 'switch_employee_to_candidate__switch_employee_to_candidate'
            )
            if (switch_to_employee) {
                dispatch(addPrefSwitchToCandidate(switch_to_employee.value))
            }

            const auto_timesheet_approval = globalPrefResponse.find(
                (item) => item.identifier === 'approve_record_automatically__approve_timesheet_record_automatically'
            )
            if (auto_timesheet_approval) {
                dispatch(addAutoTimesheetApproval(auto_timesheet_approval.value))
            }

            const allow_qr_scanner = globalPrefResponse.find(
                (item) => item.identifier === 'allow_to_scan_qr_codes__allow_to_scan_qr_codes'
            )
            if (allow_qr_scanner) {
                dispatch(addAllowQRScanner(allow_qr_scanner.value))
            }

            const allowJumpOverStages = globalPrefResponse.find(
                (item) => item.identifier === 'candidates__allow_jump_over_stages'
            )
            if (allowJumpOverStages) {
                dispatch(addAllowJumpOverStages(allowJumpOverStages.value))
            }
        }

        if (userPreferences.status === 200) {
            const userPrefResponse = userPreferences.response

            const timezone_format = userPrefResponse.find((item) => item.identifier === 'timezone__timezone')
            if (timezone_format) {
                dispatch(addTimezoneFormat(timezone_format.value))
            }

            const pref_language = userPrefResponse.find(
                (item) => item.identifier === 'language__user_language_preferences'
            )
            if (pref_language) {
                const language = normalizeUiLanguage(pref_language.value)
                dispatch(addLanguage(language))
                i18n.changeLanguage(language)
            }

            const allowEmailNotifications = userPrefResponse.find(
                (item) => item.identifier === 'allow_sending_email_notification__allow_sending_email_notification'
            )
            if (allowEmailNotifications) {
                dispatch(addAllowEmailNotifications(allowEmailNotifications.value))
            }

            const pref_dashboard = userPrefResponse.find(
                (item) => item.identifier === 'add_prefered_dashboard__add_prefered_dashboard'
            )
            if (pref_dashboard) {
                dispatch(addPreferedDashboard(pref_dashboard.value))
            }
        }

        const request = await getRequest(API.COMPANIES + '?is_active=true&query={id, logo, name}')
        if (request.status === 200) {
            if (request.response.length > 0) {
                dispatch(addCompany({ ...request.response[0], logo: request?.response[0]?.logo?.file || null }))
            }
        }

        const choices = await commonService.getChoices()
        if (choices.status === 200) {
            dispatch(addChoices(choices.response))
        }
    }

    return result
}

async function refreshLogin(refreshToken) {
    // Extend user session based on refreshToken if exist else redirect user to login
    let result = await request
        .post(API.REFRESH, {
            data: {
                refresh: refreshToken,
            },
        })
        .then(function (response) {
            // store OLD Access & Refresh Token from Cookies,
            Cookies.remove('accessToken')
            Cookies.remove('refreshToken')

            setAuthTokens(response)

            return true
        })
        .catch(function (error) {
            return false
        })

    return result
}

function logout(dispatch) {
    // Perform user logout and remove user from redux store and cookies
    Cookies.remove('accessToken')
    Cookies.remove('refreshToken')
    dispatch(logoutUser())
    dispatch({ type: 'RESET_STATE' })
}

async function validateToken() {
    const token = Cookies.get('accessToken')
    let response = {}

    if (token) {
        response = await APIRequest({
            url: API.VERIFY,
            method: 'POST',
            data: {
                token: token,
            },
        })
    }

    if (response && response.status === 200) {
        return token
    } else {
        const refresh = Cookies.get('refreshToken')
        // attempt to refreshLogin with refresh token, if true then return new access token
        if (refresh) {
            const result = await refreshLogin(refresh)
            if (result) {
                return Cookies.get('accessToken')
            }
        }
        return false
    }
}

async function getAccessToken() {
    return await validateToken()
}

/* Register */
async function register(data) {
    let created
    created = await request(API.REGISTER, {
        method: 'post',
        data: data,
    })
        .then(function (response) {
            return true
        })
        .catch(function (error) {
            return false
        })

    return created
}

async function resendActivation(data) {
    let sended
    sended = await request(API.RESEND_ACTIVATION, {
        method: 'post',
        data: data,
    })
        .then(function (response) {
            return true
        })
        .catch(function (error) {
            return false
        })

    return sended
}

export async function checkUser() {
    const result = await authService.getAccessToken()
    return !result ? false : true
}
